import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { memo } from 'react';
import { Popover, notification } from 'antd';

import { updateChatRoomMessagesReadBy } from '../../../actions/chatRoomAction';
import { isChatSettingsOpen, addChatRoom } from '../../../actions';
import { RESET_COUNTER, UPDATE_CURRENT_RECEIVER } from '../../../actions/types/chat.types';
import { setRoomId } from '../../../actions/roomAction';
import { clearMessagesState, updateQueryState } from '../../../actions/messageAction';
import { getWorkspacePalette } from '../../../js/helper';

const UserProfilePopover = ({ children, member, currentWorkspace }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const chats = useSelector((state) => state.chatReducer.chats);

  const openChat = async (userId) => {
    const existingChat = chats?.find(
      (chat) =>
        chat?.roomType === 'direct' &&
        chat?.members?.find((mem) => mem?._id === userId)
    );

    if (existingChat) {
      dispatch(updateQueryState(1));
      dispatch(clearMessagesState());
      dispatch(setRoomId(existingChat?._id));
      dispatch({ type: RESET_COUNTER, payload: existingChat?._id });
      navigate(`/chat/${existingChat?._id}`, { replace: true });
      dispatch({ type: UPDATE_CURRENT_RECEIVER, payload: existingChat?.roomName });
      dispatch(updateChatRoomMessagesReadBy(existingChat?._id, 'ChatsNavBar1'));
    } else {
      dispatch(addChatRoom({ members: [userId], roomName: null, workspaceId: currentWorkspace }));
    }
    dispatch(isChatSettingsOpen(false));
  };

  const hasRealAvi = member?.avi && !member.avi.includes('no_avi.png');
  const palette = getWorkspacePalette(member?.fullName || '');
  const initials = member?.fullName?.slice(0, 2)?.toUpperCase() || '';

  return (
    <Popover
      content={
        !member?._id ? '' :
        <div style={{ width: 300 }}>
          {/* Header */}
          <div
            className='flex items-center gap-3 px-4 py-4 cursor-pointer'
            style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
            onClick={() => navigate(`/profile/${member?._id}`)}
          >
            {hasRealAvi ? (
              <div style={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                <img
                  src={member.avi}
                  alt={member.fullName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            ) : (
              <div
                className='flex items-center justify-center font-bold text-sm flex-shrink-0'
                style={{ width: 48, height: 48, borderRadius: '50%', background: palette.bg, color: palette.text, letterSpacing: '0.04em' }}
              >
                {initials}
              </div>
            )}
            <div className='min-w-0'>
              <div className='font-semibold text-sm text-slate-800 truncate'>{member?.fullName}</div>
              <div className='text-xs text-slate-400 mt-0.5 capitalize'>{member?.role}</div>
              <div className='text-xs text-slate-500 truncate mt-0.5'>{member?.email}</div>
            </div>
          </div>
          {/* Actions */}
          <div className='flex gap-1.5 px-3 py-2.5'>
            <button
              className='flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors flex-1 justify-center'
              style={{ background: 'rgba(99,102,241,0.08)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.2)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
              onClick={() => openChat(member?._id)}
            >
              <img src={'/images/icons/message.svg'} alt="" style={{ width: 13, height: 13, opacity: 0.8 }} />
              <FormattedMessage id="CHAT" defaultMessage="Chat" />
            </button>
            <button
              className='flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors flex-1 justify-center'
              style={{ background: 'rgba(0,0,0,0.04)', color: '#475569', border: '1px solid rgba(0,0,0,0.08)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
              onClick={() => {
                navigator.clipboard.writeText(member.email || '');
                notification.success({
                  message: intl.formatMessage({ id: 'EMAIL_COPIED_TO_CLIPBOARD', defaultMessage: 'Email copied to clipboard' }),
                  placement: 'bottomRight',
                });
              }}
            >
              <img src={'/images/icons/copy.svg'} alt="" style={{ width: 13, height: 13, opacity: 0.6 }} />
              <FormattedMessage id="COPY_EMAIL" defaultMessage="Copy Email" />
            </button>
          </div>
        </div>
      }
      trigger="hover"
      arrow={false}
      overlayInnerStyle={{ padding: 0, borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
    >
      {children}
    </Popover>
  );
};

export default memo(UserProfilePopover);
